import React from 'react';
import { t } from '../lib/i18n';

/**
 * TrendChart -- SVG line chart showing audit score trend over time.
 *
 * Props:
 *   filteredTrend: Array<{ score: number, label: string }>  -- at least 2 data points
 *
 * Uses copper-branded line (#AA5D26) and score-based dot colors.
 */

import { scoreHexColor } from '../lib/scoring';

function scoreColor(s) {
    return scoreHexColor(s);
}

export default function TrendChart({ filteredTrend }) {
    if (!filteredTrend || filteredTrend.length < 2) {
        return <p className="text-sm text-slate-400 text-center py-8">{t('reports_no_data')}</p>;
    }

    const W = 600;
    const H = 200;
    const PAD = 40;
    const minY = 0;
    const maxY = 10;
    const xStep = (W - PAD * 2) / (filteredTrend.length - 1);

    const points = filteredTrend.map((d, i) => ({
        x: PAD + i * xStep,
        y: PAD + (1 - (d.score - minY) / (maxY - minY)) * (H - PAD * 2),
        ...d,
    }));

    const polyline = points.map(p => `${p.x},${p.y}`).join(' ');

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: '220px' }}>
            {/* Grid lines */}
            {[0, 2, 4, 6, 8, 10].map(v => {
                const y = PAD + (1 - v / 10) * (H - PAD * 2);
                return (
                    <g key={'g' + v}>
                        <line
                            x1={PAD} y1={y} x2={W - PAD} y2={y}
                            stroke="currentColor" strokeWidth={0.5}
                            className="text-slate-200 dark:text-slate-700"
                        />
                        <text
                            x={PAD - 8} y={y + 4} textAnchor="end"
                            className="text-slate-400 dark:text-slate-500"
                            style={{ fontSize: '10px' }}
                        >
                            {v}
                        </text>
                    </g>
                );
            })}

            {/* Line */}
            <polyline
                points={polyline}
                fill="none"
                stroke="#AA5D26"
                strokeWidth={2.5}
                strokeLinejoin="round"
                strokeLinecap="round"
            />

            {/* Points */}
            {points.map((p, i) => (
                <g key={'p' + i}>
                    <circle cx={p.x} cy={p.y} r={5} fill={scoreColor(p.score)} stroke="#fff" strokeWidth={2} />
                    <text
                        x={p.x} y={H - 8} textAnchor="middle"
                        className="text-slate-400 dark:text-slate-500"
                        style={{ fontSize: '9px' }}
                    >
                        {p.label}
                    </text>
                </g>
            ))}
        </svg>
    );
}
